import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfWeek } from "date-fns";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./components/ui/table";
import { Button } from "./components/ui/button";
import {
  Download, Search, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight,
  ChevronDown, ChevronUp, Loader2,
  Truck, Building2, Package, IndianRupee, Hash, Layers, Calendar,
  FileText, ShoppingCart,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `\u20B9${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtC = (v) => {
  if (!v) return "\u20B90";
  if (v >= 10000000) return `\u20B9${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return fmt(v);
};
const fmtD = (s) => {
  if (!s) return "-";
  try {
    const d = new Date(s.includes("T") ? s : s + "T00:00:00+05:30");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch { return "-"; }
};

const MC = {
  "Opening Stock": "bg-blue-100 text-blue-700",
  "Stock Adjustment": "bg-amber-100 text-amber-700",
  "Stock Receipt": "bg-emerald-100 text-emerald-700",
  "Consumption": "bg-orange-100 text-orange-700",
  "Return to Vendor": "bg-pink-100 text-pink-700",
  "Breakage": "bg-red-100 text-red-700",
  "Damage": "bg-red-100 text-red-700",
  "Expiry Return": "bg-rose-100 text-rose-700",
  "Scrap": "bg-stone-100 text-stone-700",
  "Transfer to Other Branch": "bg-violet-100 text-violet-700",
  "Implementation Adjustment": "bg-teal-100 text-teal-700",
  "Return": "bg-pink-100 text-pink-700",
  "Inter Company Transfer": "bg-indigo-100 text-indigo-700",
  "Interdepartment Transfer": "bg-purple-100 text-purple-700",
};
const MovBadge = ({ label }) => (
  <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ${MC[label] || "bg-slate-100 text-slate-600"}`}>{label}</span>
);

const MODE_CFG = {
  summary: { label: "By Supplier" },
  detailed: { label: "Detailed" },
  document: { label: "By Document" },
  item: { label: "By Item" },
};

export default function StockReport() {
  const [mode, setMode] = useState("summary");
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [grand, setGrand] = useState({});
  const [loading, setLoading] = useState(true);

  const [dateFrom, setDateFrom] = useState(() => format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [supplier, setSupplier] = useState("");
  const [movementType, setMovementType] = useState("103");
  const [itemSearch, setItemSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterOpts, setFilterOpts] = useState({ suppliers: [], movement_types: [], departments: [] });

  // Drill-down state
  const [expanded, setExpanded] = useState({});
  const [drillData, setDrillData] = useState({});
  const [drillLoading, setDrillLoading] = useState({});

  // Abort controller for cancelling stale requests
  const abortRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/inventory/stock-report/filters`).then(r => setFilterOpts(r.data)).catch(() => {});
  }, []);

  // Debounce item search — 400ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(itemSearch), 400);
    return () => clearTimeout(t);
  }, [itemSearch]);

  const filterParams = useMemo(() => {
    const p = {};
    if (supplier) p.supplier = supplier;
    if (movementType) p.movement_type = movementType;
    if (debouncedSearch) p.item_name = debouncedSearch;
    return p;
  }, [supplier, movementType, debouncedSearch]);

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setExpanded({});
    setDrillData({});
    try {
      const params = { mode, page, page_size: 50, date_from: dateFrom, date_to: dateTo, ...filterParams };
      const { data: res } = await axios.get(`${API}/api/inventory/stock-report`, { params, signal: controller.signal });
      setData(res.data);
      setTotal(res.total);
      setTotalPages(res.total_pages);
      setGrand(res.grand || {});
    } catch (err) {
      if (!axios.isCancel(err)) toast.error("Failed to load stock report");
    }
    setLoading(false);
  }, [mode, page, dateFrom, dateTo, filterParams]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [mode, dateFrom, dateTo, supplier, movementType, debouncedSearch]);

  const toggleDrill = useCallback(async (rowKey, drillMode) => {
    if (expanded[rowKey]) {
      setExpanded(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    setExpanded(prev => ({ ...prev, [rowKey]: true }));
    if (drillData[rowKey]) return; // already cached

    setDrillLoading(prev => ({ ...prev, [rowKey]: true }));
    try {
      const params = { mode: drillMode, key: rowKey, date_from: dateFrom, date_to: dateTo, ...filterParams };
      const { data: res } = await axios.get(`${API}/api/inventory/stock-report/drill-down`, { params });
      setDrillData(prev => ({ ...prev, [rowKey]: res.rows || [] }));
    } catch { toast.error("Failed to load details"); }
    setDrillLoading(prev => ({ ...prev, [rowKey]: false }));
  }, [expanded, drillData, dateFrom, dateTo, filterParams]);

  const handleExport = async () => {
    try {
      toast.info("Generating CSV...");
      const params = { mode, date_from: dateFrom, date_to: dateTo, ...filterParams };
      const r = await axios.get(`${API}/api/inventory/stock-report/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `stock_report_${mode}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  // Group detailed data by supplier
  const groupedDetailData = useMemo(() => {
    if (mode !== "detailed") return [];
    const groups = {};
    for (const row of data) {
      const key = row.supplier || "Unknown";
      if (!groups[key]) groups[key] = { supplier: key, items: [], totalValue: 0, totalQty: 0 };
      groups[key].items.push(row);
      groups[key].totalValue += row.totalValue;
      groups[key].totalQty += row.totalQty;
    }
    return Object.values(groups);
  }, [data, mode]);

  const kpiCards = useMemo(() => {
    const g = grand;
    const base = [
      { icon: IndianRupee, label: "Total Value", value: fmtC(g.totalValue || 0), bg: "bg-slate-900", textColor: "text-white", iconColor: "text-slate-400" },
      { icon: Package, label: "Total Qty", value: (g.totalQty || 0).toLocaleString("en-IN"), color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
    ];
    if (mode === "summary") return [...base,
      { icon: Hash, label: "Item Lines", value: (g.itemCount || 0).toLocaleString("en-IN"), color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
      { icon: Truck, label: "Transactions", value: (g.transactionCount || 0).toLocaleString("en-IN"), color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
      { icon: Layers, label: "Tax Amt", value: fmtC(g.totalTax || 0), color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
      { icon: Building2, label: "Suppliers", value: (g.supplierCount || 0).toLocaleString("en-IN"), color: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
    ];
    if (mode === "detailed") return [...base,
      { icon: Hash, label: "Unique Items", value: (g.uniqueItems || 0).toLocaleString("en-IN"), color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
      { icon: Building2, label: "Suppliers", value: (g.uniqueSuppliers || 0).toLocaleString("en-IN"), color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
      { icon: Layers, label: "Tax Amt", value: fmtC(g.totalTax || 0), color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
      { icon: Truck, label: "Line Items", value: (g.lineCount || 0).toLocaleString("en-IN"), color: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
    ];
    if (mode === "document") return [...base,
      { icon: FileText, label: "Documents", value: (g.documentCount || 0).toLocaleString("en-IN"), color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
      { icon: Hash, label: "Item Lines", value: (g.itemCount || 0).toLocaleString("en-IN"), color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
      { icon: Layers, label: "Tax Amt", value: fmtC(g.totalTax || 0), color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
      { icon: Building2, label: "Suppliers", value: (g.uniqueSuppliers || 0).toLocaleString("en-IN"), color: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
    ];
    return [...base,
      { icon: ShoppingCart, label: "Unique Items", value: (g.uniqueItems || 0).toLocaleString("en-IN"), color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
      { icon: Truck, label: "Line Count", value: (g.lineCount || 0).toLocaleString("en-IN"), color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
      { icon: Layers, label: "Tax Amt", value: fmtC(g.totalTax || 0), color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
      { icon: Building2, label: "Suppliers", value: (g.uniqueSuppliers || 0).toLocaleString("en-IN"), color: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
    ];
  }, [mode, grand]);

  const statusLabel = mode === "summary" ? `${total} suppliers`
    : mode === "detailed" ? `${total} item groups`
    : mode === "document" ? `${total} documents`
    : `${total} items`;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stock-filters">
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="From">
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 transition-all" data-testid="stock-date-from" />
            </div>
          </FilterField>
          <FilterField label="To">
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-40 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 transition-all" data-testid="stock-date-to" />
            </div>
          </FilterField>
          <FilterField label="Supplier">
            <select value={supplier} onChange={e => setSupplier(e.target.value)}
              className="w-52 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 transition-all" data-testid="stock-supplier">
              <option value="">All Suppliers</option>
              {filterOpts.suppliers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FilterField>
          <FilterField label="Movement Type">
            <select value={movementType} onChange={e => setMovementType(e.target.value)}
              className="w-52 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 transition-all" data-testid="stock-movement-type">
              <option value="">All Types</option>
              {filterOpts.movement_types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FilterField>
          <FilterField label="Item Name">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Search item..."
                className="w-48 pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10 transition-all" data-testid="stock-item-search" />
            </div>
          </FilterField>
          <div className="flex items-end gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={handleExport} data-testid="stock-export-csv" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Mode Toggle + KPIs */}
      <div className="flex items-start gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shrink-0 w-52">
          <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 block">Report Mode</label>
          <div className="space-y-2">
            {Object.entries(MODE_CFG).map(([key, cfg]) => (
              <button key={key} onClick={() => setMode(key)} data-testid={`mode-${key}`}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === key ? "bg-slate-900 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {mode === key ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stock-kpis">
          {kpiCards.map((kpi, i) => (
            <div key={i} className={`${kpi.bg} ${kpi.border ? `border ${kpi.border}` : ""} rounded-xl p-3.5`} data-testid={`stock-kpi-${i}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <kpi.icon className={`w-3.5 h-3.5 ${kpi.iconColor || kpi.color}`} />
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${kpi.bg === "bg-slate-900" ? "text-slate-400" : "text-slate-500"}`}>{kpi.label}</span>
              </div>
              <p className={`text-lg font-bold ${kpi.textColor || kpi.color}`}>{kpi.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="stock-table">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500 font-medium">
            {statusLabel} | Page {page} of {totalPages || 1}
            {mode !== "detailed" && <span className="text-slate-400 ml-2">(click row to expand details)</span>}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="stock-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="stock-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {mode === "summary" && <SummaryTable data={data} loading={loading} grand={grand} expanded={expanded} drillData={drillData} drillLoading={drillLoading} toggleDrill={toggleDrill} />}
          {mode === "detailed" && <DetailedTable groups={groupedDetailData} loading={loading} grand={grand} data={data} />}
          {mode === "document" && <DocumentTable data={data} loading={loading} grand={grand} expanded={expanded} drillData={drillData} drillLoading={drillLoading} toggleDrill={toggleDrill} />}
          {mode === "item" && <ItemTable data={data} loading={loading} grand={grand} expanded={expanded} drillData={drillData} drillLoading={drillLoading} toggleDrill={toggleDrill} />}
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs font-semibold text-slate-600 px-2 py-1">{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

/* ─── Expand/Collapse chevron ─── */
function ExpandIcon({ isOpen }) {
  return isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />;
}

/* ─── Drill-down sub-table container ─── */
function DrillPanel({ colSpan, rows, drillLoading, children }) {
  return (
    <TableRow className="bg-slate-50/80">
      <TableCell colSpan={colSpan} className="p-0">
        <div className="border-l-4 border-indigo-300 ml-4 my-2">
          {drillLoading ? (
            <div className="flex items-center gap-2 px-4 py-4 text-xs text-slate-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading details...
            </div>
          ) : rows && rows.length > 0 ? (
            <div className="overflow-x-auto">
              {children}
            </div>
          ) : (
            <div className="px-4 py-3 text-xs text-slate-400">No detail records found</div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/* ═══════════════════════════════════════════
   SUMMARY TABLE (By Supplier) + Drill-down
   ═══════════════════════════════════════════ */
function SummaryTable({ data, loading, grand, expanded, drillData, drillLoading, toggleDrill }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead className="w-8"></TableHead>
          {["Supplier", "Transactions", "Item Lines", "Total Qty", "Total Value", "Tax Amount", "Discount Amt", "Movement Breakdown"].map(h => (
            <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>}
        {!loading && data.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">No data found for selected filters</TableCell></TableRow>}
        {!loading && data.map((row, i) => {
          const key = row.supplier || "Unknown";
          const isOpen = !!expanded[key];
          return (
            <React.Fragment key={`summary-${i}`}>
              <TableRow className={`cursor-pointer transition-colors ${isOpen ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                onClick={() => toggleDrill(key, "summary")} data-testid={`summary-row-${i}`}>
                <TableCell className="w-8 px-2"><ExpandIcon isOpen={isOpen} /></TableCell>
                <TableCell className="text-xs font-semibold text-slate-800 max-w-[200px]">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center shrink-0">
                      <Building2 className="w-3 h-3 text-indigo-600" />
                    </div>
                    <span className="truncate">{key}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-center tabular-nums font-medium">{row.transactionCount}</TableCell>
                <TableCell className="text-xs text-center tabular-nums">{row.itemCount}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{row.totalQty.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-xs text-right tabular-nums font-bold text-slate-800">{fmt(row.totalValue)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(row.totalTax)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(row.totalDiscount)}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(row.movements || []).map((m, mi) => (
                      <span key={mi} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${MC[m.label] || "bg-slate-100 text-slate-600"}`}>
                        {m.label}: {m.items}
                      </span>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
              {isOpen && (
                <DrillPanel colSpan={9} rows={drillData[key]} drillLoading={drillLoading[key]}>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-indigo-50/50">
                        {["Stock Number", "Date", "Movement Type", "Invoice #", "Department", "Received By", "Items", "Total Qty", "Total Value", "Tax Amt", "Disc Amt"].map(h => (
                          <TableHead key={h} className="text-[9px] font-bold uppercase tracking-wider text-indigo-500 whitespace-nowrap py-1.5 px-2">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(drillData[key] || []).map((d, di) => (
                        <TableRow key={di} className="hover:bg-white/60">
                          <TableCell className="text-[11px] font-mono text-slate-700 px-2 py-1.5">{d.stockNumber}</TableCell>
                          <TableCell className="text-[11px] tabular-nums px-2 py-1.5">{fmtD(d.date)}</TableCell>
                          <TableCell className="px-2 py-1.5"><MovBadge label={d.movementLabel} /></TableCell>
                          <TableCell className="text-[11px] font-mono text-slate-500 px-2 py-1.5">{d.invoiceNumber || "-"}</TableCell>
                          <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{d.department || "-"}</TableCell>
                          <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{d.receivedBy || "-"}</TableCell>
                          <TableCell className="text-[11px] text-center tabular-nums px-2 py-1.5">{d.itemCount}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{d.totalQty}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums font-semibold px-2 py-1.5">{fmt(d.totalValue)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums text-amber-700 px-2 py-1.5">{fmt(d.totalTax)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(d.totalDiscount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DrillPanel>
              )}
            </React.Fragment>
          );
        })}
        {!loading && data.length > 0 && (
          <TableRow className="bg-slate-900" data-testid="summary-total-row">
            <TableCell></TableCell>
            <TableCell className="text-xs font-bold text-white">GRAND TOTAL ({grand.supplierCount} suppliers)</TableCell>
            <TableCell className="text-xs text-center tabular-nums font-bold text-white">{grand.transactionCount}</TableCell>
            <TableCell className="text-xs text-center tabular-nums font-bold text-white">{grand.itemCount}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{(grand.totalQty || 0).toLocaleString("en-IN")}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalValue)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-amber-300">{fmt(grand.totalTax)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalDiscount)}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

/* ═══════════════════════════════════════════
   DETAILED TABLE (Supplier → Item) — already grouped, no drill-down needed
   ═══════════════════════════════════════════ */
function DetailedTable({ groups, loading, grand, data }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          {["Supplier / Item", "Code", "Category", "UOM", "Lines", "Total Qty", "Avg Unit Price", "Total Value", "Tax Amt", "Discount Amt", "Batches", "Movements", "Last Date"].map(h => (
            <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && <TableRow><TableCell colSpan={13} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>}
        {!loading && groups.length === 0 && <TableRow><TableCell colSpan={13} className="text-center py-10 text-slate-400">No data found</TableCell></TableRow>}
        {!loading && groups.map((group, gi) => (
          <GroupRows key={gi} group={group} gi={gi} />
        ))}
        {!loading && data.length > 0 && (
          <TableRow className="bg-slate-900" data-testid="detail-total-row">
            <TableCell className="text-xs font-bold text-white">GRAND TOTAL ({grand.uniqueSuppliers} suppliers, {grand.uniqueItems} items)</TableCell>
            <TableCell colSpan={4}></TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{(grand.totalQty || 0).toLocaleString("en-IN")}</TableCell>
            <TableCell></TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalValue)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-amber-300">{fmt(grand.totalTax)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalDiscount)}</TableCell>
            <TableCell colSpan={3}></TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function GroupRows({ group, gi }) {
  return (
    <>
      <TableRow className="bg-indigo-50 border-t-2 border-indigo-200" data-testid={`detail-group-${gi}`}>
        <TableCell colSpan={5} className="text-xs font-bold text-indigo-800">
          <div className="flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-indigo-600" />
            {group.supplier} ({group.items.length} items)
          </div>
        </TableCell>
        <TableCell className="text-xs text-right tabular-nums font-bold text-indigo-800">{group.totalQty.toLocaleString("en-IN")}</TableCell>
        <TableCell></TableCell>
        <TableCell className="text-xs text-right tabular-nums font-bold text-indigo-800">{fmt(group.totalValue)}</TableCell>
        <TableCell colSpan={5}></TableCell>
      </TableRow>
      {group.items.map((row, ri) => (
        <TableRow key={`${gi}-${ri}`} className="hover:bg-slate-50 transition-colors" data-testid={`detail-row-${gi}-${ri}`}>
          <TableCell className="text-xs font-medium text-slate-800 max-w-[200px] truncate pl-8">{row.itemName}</TableCell>
          <TableCell className="text-xs font-mono text-slate-500">{row.itemCode || "-"}</TableCell>
          <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">{row.category || "-"}</TableCell>
          <TableCell className="text-xs">{row.uom || "-"}</TableCell>
          <TableCell className="text-xs text-center tabular-nums">{row.lineCount}</TableCell>
          <TableCell className="text-xs text-right tabular-nums font-medium">{row.totalQty.toLocaleString("en-IN")}</TableCell>
          <TableCell className="text-xs text-right tabular-nums">{fmt(row.avgUnitPrice)}</TableCell>
          <TableCell className="text-xs text-right tabular-nums font-semibold text-slate-800">{fmt(row.totalValue)}</TableCell>
          <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(row.totalTax)}</TableCell>
          <TableCell className="text-xs text-right tabular-nums">{fmt(row.totalDiscount)}</TableCell>
          <TableCell className="text-xs text-center">{row.batchCount || 0}</TableCell>
          <TableCell className="text-xs">
            <div className="flex flex-wrap gap-0.5">
              {(row.movementLabels || "").split(", ").map((m, mi) => m && (
                <span key={mi} className={`px-1 py-0.5 rounded text-[8px] font-semibold ${MC[m] || "bg-slate-100 text-slate-600"}`}>{m}</span>
              ))}
            </div>
          </TableCell>
          <TableCell className="text-xs whitespace-nowrap">{fmtD(row.lastDate)}</TableCell>
        </TableRow>
      ))}
    </>
  );
}

/* ═══════════════════════════════════════════
   DOCUMENT TABLE (By Stock Number) + Drill-down to line items
   ═══════════════════════════════════════════ */
function DocumentTable({ data, loading, grand, expanded, drillData, drillLoading, toggleDrill }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead className="w-8"></TableHead>
          {["Stock Number", "Date", "Supplier", "Movement Type", "Invoice #", "Department", "Items", "Total Qty", "Total Value", "Tax Amt", "Discount Amt"].map(h => (
            <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && <TableRow><TableCell colSpan={12} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>}
        {!loading && data.length === 0 && <TableRow><TableCell colSpan={12} className="text-center py-10 text-slate-400">No data found for selected filters</TableCell></TableRow>}
        {!loading && data.map((row, i) => {
          const key = row.stockNumber;
          const isOpen = !!expanded[key];
          return (
            <React.Fragment key={`document-${i}`}>
              <TableRow className={`cursor-pointer transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-slate-50"}`}
                onClick={() => toggleDrill(key, "document")} data-testid={`document-row-${i}`}>
                <TableCell className="w-8 px-2"><ExpandIcon isOpen={isOpen} /></TableCell>
                <TableCell className="text-xs font-semibold text-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center shrink-0">
                      <FileText className="w-3 h-3 text-blue-600" />
                    </div>
                    <span className="font-mono">{row.stockNumber || "-"}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap tabular-nums">{fmtD(row.date)}</TableCell>
                <TableCell className="text-xs text-slate-700 max-w-[180px] truncate">{row.supplier || "-"}</TableCell>
                <TableCell className="text-xs"><MovBadge label={row.movementLabel} /></TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{row.invoiceNumber || "-"}</TableCell>
                <TableCell className="text-xs text-slate-500">{row.department || "-"}</TableCell>
                <TableCell className="text-xs text-center tabular-nums font-medium">{row.itemCount}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{row.totalQty.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-xs text-right tabular-nums font-bold text-slate-800">{fmt(row.totalValue)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(row.totalTax)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(row.totalDiscount)}</TableCell>
              </TableRow>
              {isOpen && (
                <DrillPanel colSpan={12} rows={drillData[key]} drillLoading={drillLoading[key]}>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-blue-50/50">
                        {["Item Name", "Item Code", "Category", "UOM", "Batch #", "Qty", "Unit Price", "MRP", "Total Value", "Tax %", "Tax Amt", "Disc %", "Disc Amt", "Expiry"].map(h => (
                          <TableHead key={h} className="text-[9px] font-bold uppercase tracking-wider text-blue-500 whitespace-nowrap py-1.5 px-2">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(drillData[key] || []).map((d, di) => (
                        <TableRow key={di} className="hover:bg-white/60">
                          <TableCell className="text-[11px] font-medium text-slate-800 px-2 py-1.5 max-w-[180px] truncate">{d.itemName}</TableCell>
                          <TableCell className="text-[11px] font-mono text-slate-500 px-2 py-1.5">{d.itemCode || "-"}</TableCell>
                          <TableCell className="text-[11px] text-slate-500 px-2 py-1.5 max-w-[100px] truncate">{d.category || "-"}</TableCell>
                          <TableCell className="text-[11px] px-2 py-1.5">{d.uom || "-"}</TableCell>
                          <TableCell className="text-[11px] font-mono text-slate-500 px-2 py-1.5">{d.batchNumber || "-"}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums font-medium px-2 py-1.5">{d.quantity}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(d.unitPrice)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(d.mrp)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums font-semibold px-2 py-1.5">{fmt(d.totalValue)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums text-amber-600 px-2 py-1.5">{d.taxPct}%</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums text-amber-700 px-2 py-1.5">{fmt(d.taxAmt)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{d.discountPct}%</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(d.discountAmt)}</TableCell>
                          <TableCell className="text-[11px] px-2 py-1.5 whitespace-nowrap">{fmtD(d.expiryDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DrillPanel>
              )}
            </React.Fragment>
          );
        })}
        {!loading && data.length > 0 && (
          <TableRow className="bg-slate-900" data-testid="document-total-row">
            <TableCell></TableCell>
            <TableCell className="text-xs font-bold text-white">GRAND TOTAL ({grand.documentCount} documents)</TableCell>
            <TableCell></TableCell>
            <TableCell className="text-xs font-bold text-white">{grand.uniqueSuppliers} suppliers</TableCell>
            <TableCell colSpan={3}></TableCell>
            <TableCell className="text-xs text-center tabular-nums font-bold text-white">{grand.itemCount}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{(grand.totalQty || 0).toLocaleString("en-IN")}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalValue)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-amber-300">{fmt(grand.totalTax)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalDiscount)}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

/* ═══════════════════════════════════════════
   ITEM TABLE (By Item) + Drill-down to doc/supplier occurrences
   ═══════════════════════════════════════════ */
function ItemTable({ data, loading, grand, expanded, drillData, drillLoading, toggleDrill }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead className="w-8"></TableHead>
          {["Item Name", "Code", "Category", "UOM", "Suppliers", "Lines", "Total Qty", "Avg Price", "Total Value", "Tax Amt", "Discount Amt", "Batches", "Movements", "Last Date"].map(h => (
            <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && <TableRow><TableCell colSpan={15} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>}
        {!loading && data.length === 0 && <TableRow><TableCell colSpan={15} className="text-center py-10 text-slate-400">No data found for selected filters</TableCell></TableRow>}
        {!loading && data.map((row, i) => {
          const key = row.itemName;
          const isOpen = !!expanded[key];
          return (
            <React.Fragment key={`item-${i}`}>
              <TableRow className={`cursor-pointer transition-colors ${isOpen ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                onClick={() => toggleDrill(key, "item")} data-testid={`item-row-${i}`}>
                <TableCell className="w-8 px-2"><ExpandIcon isOpen={isOpen} /></TableCell>
                <TableCell className="text-xs font-semibold text-slate-800 max-w-[200px]">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center shrink-0">
                      <ShoppingCart className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="truncate">{row.itemName || "Unknown"}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{row.itemCode || "-"}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">{row.category || "-"}</TableCell>
                <TableCell className="text-xs">{row.uom || "-"}</TableCell>
                <TableCell className="text-xs text-center tabular-nums font-medium" title={row.supplierNames || ""}>{row.supplierCount}</TableCell>
                <TableCell className="text-xs text-center tabular-nums">{row.lineCount}</TableCell>
                <TableCell className="text-xs text-right tabular-nums font-medium">{row.totalQty.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(row.avgUnitPrice)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums font-bold text-slate-800">{fmt(row.totalValue)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-amber-700">{fmt(row.totalTax)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{fmt(row.totalDiscount)}</TableCell>
                <TableCell className="text-xs text-center">{row.batchCount || 0}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-wrap gap-0.5">
                    {(row.movementLabels || "").split(", ").map((m, mi) => m && (
                      <span key={mi} className={`px-1 py-0.5 rounded text-[8px] font-semibold ${MC[m] || "bg-slate-100 text-slate-600"}`}>{m}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">{fmtD(row.lastDate)}</TableCell>
              </TableRow>
              {isOpen && (
                <DrillPanel colSpan={15} rows={drillData[key]} drillLoading={drillLoading[key]}>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-emerald-50/50">
                        {["Stock Number", "Date", "Supplier", "Movement Type", "Invoice #", "Department", "Batch #", "Qty", "Unit Price", "Total Value", "Tax %", "Tax Amt", "Disc %", "Expiry"].map(h => (
                          <TableHead key={h} className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 whitespace-nowrap py-1.5 px-2">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(drillData[key] || []).map((d, di) => (
                        <TableRow key={di} className="hover:bg-white/60">
                          <TableCell className="text-[11px] font-mono text-slate-700 px-2 py-1.5">{d.stockNumber}</TableCell>
                          <TableCell className="text-[11px] tabular-nums px-2 py-1.5">{fmtD(d.date)}</TableCell>
                          <TableCell className="text-[11px] text-slate-700 px-2 py-1.5 max-w-[150px] truncate">{d.supplier || "-"}</TableCell>
                          <TableCell className="px-2 py-1.5"><MovBadge label={d.movementLabel} /></TableCell>
                          <TableCell className="text-[11px] font-mono text-slate-500 px-2 py-1.5">{d.invoiceNumber || "-"}</TableCell>
                          <TableCell className="text-[11px] text-slate-500 px-2 py-1.5">{d.department || "-"}</TableCell>
                          <TableCell className="text-[11px] font-mono text-slate-500 px-2 py-1.5">{d.batchNumber || "-"}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums font-medium px-2 py-1.5">{d.quantity}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{fmt(d.unitPrice)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums font-semibold px-2 py-1.5">{fmt(d.totalValue)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums text-amber-600 px-2 py-1.5">{d.taxPct}%</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums text-amber-700 px-2 py-1.5">{fmt(d.taxAmt)}</TableCell>
                          <TableCell className="text-[11px] text-right tabular-nums px-2 py-1.5">{d.discountPct}%</TableCell>
                          <TableCell className="text-[11px] px-2 py-1.5 whitespace-nowrap">{fmtD(d.expiryDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DrillPanel>
              )}
            </React.Fragment>
          );
        })}
        {!loading && data.length > 0 && (
          <TableRow className="bg-slate-900" data-testid="item-total-row">
            <TableCell></TableCell>
            <TableCell className="text-xs font-bold text-white">GRAND TOTAL ({grand.uniqueItems} items)</TableCell>
            <TableCell colSpan={5}></TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{(grand.totalQty || 0).toLocaleString("en-IN")}</TableCell>
            <TableCell></TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalValue)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-amber-300">{fmt(grand.totalTax)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-white">{fmt(grand.totalDiscount)}</TableCell>
            <TableCell colSpan={3}></TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
